import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { ThemeService } from '../../services/theme.service';
import { ProgressionFinancialMetric } from '../../services/apis/dashboard/dashboard.service';
import { chartColors, kpiColor } from './chart-theme';

interface WaterfallStep {
  label: string;
  start: number;
  end: number;
  displayValue: number;
  color: string;
}

export interface ProgressionMetricSelection {
  metric: ProgressionFinancialMetric;
  period: 'current' | 'comparison';
}

@Component({
  selector: 'app-kpi-waterfall-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  host: { class: 'block h-full w-full min-w-0' },
  templateUrl: './kpi-waterfall-chart.component.html',
})
export class KpiWaterfallChartComponent implements AfterViewInit, OnChanges, OnDestroy, OnInit {
  @Input() metrics: ProgressionFinancialMetric[] = [];
  @Input() comparisonMetrics: ProgressionFinancialMetric[] = [];
  @Output() metricClick = new EventEmitter<ProgressionMetricSelection>();

  options: EChartsOption | null = null;

  @ViewChild('chartContainer', { static: true }) private chartContainer?: ElementRef<HTMLElement>;

  private readonly themeService = inject(ThemeService);
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private chartInstance?: { resize: () => void };

  ngOnInit(): void {
    this.buildOptions();
    this.themeSub = this.themeService.isDark$.pipe(skip(1)).subscribe(() => this.buildOptions());
  }

  ngOnChanges(): void {
    this.buildOptions();
  }

  ngAfterViewInit(): void {
    if (!this.chartContainer?.nativeElement) return;
    this.resizeObserver = new ResizeObserver(() => this.chartInstance?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  onChartInit(instance: { resize: () => void }): void {
    this.chartInstance = instance;
    this.chartInstance.resize();
  }

  onChartClick(params: any): void {
    if (!['KPI bridge', 'Current', 'Comparison'].includes(params?.seriesName)) return;
    const period: 'current' | 'comparison' = params?.seriesName === 'Comparison' ? 'comparison' : 'current';

    const metric = (period === 'comparison' ? this.visibleComparisonMetrics() : this.visibleMetrics())[params?.dataIndex];
    if (
      !metric ||
      ![
        'revenue',
        'cost_of_sales',
        'operating_cost',
        'nfi',
        'ebitda',
        'net_profit',
      ].includes(metric.key)
    ) return;

    this.metricClick.emit({ metric, period });
  }

  private buildOptions(): void {
    const steps = this.buildSteps();
    const c = chartColors();

    if (steps.length === 0) {
      this.options = null;
      return;
    }

    if (this.comparisonMetrics.length > 0) {
      const current = this.visibleMetrics();
      const comparisonByKey = new Map(this.comparisonMetrics.map(metric => [metric.key, metric]));
      const comparisonColor = document.documentElement.classList.contains('dark') ? '#A3E635' : '#65A30D';
      this.options = {
        animation: false,
        grid: { left: 34, right: 20, top: 24, bottom: 76, containLabel: true },
        legend: {
          data: ['Current', 'Comparison'],
          left: 'center',
          bottom: 8,
          textStyle: { color: c.textSecondary },
        },
        tooltip: { trigger: 'axis', confine: true, backgroundColor: c.cardBg, borderColor: c.cardBorder, textStyle: { color: c.textPrimary } },
        xAxis: {
          type: 'category',
          data: current.map(metric => metric.key === 'operating_cost' ? 'Total Administrative Cost' : metric.name),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: c.cardBorder } },
          axisLabel: { color: c.textSecondary, interval: 0 },
        },
        yAxis: {
          type: 'value',
          axisLabel: { color: c.textSecondary, formatter: (value: number) => this.formatAxis(value) },
          splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed' } },
        },
        series: [
          {
            name: 'Current', type: 'bar', cursor: 'pointer', barMaxWidth: 46,
            data: current.map(metric => this.displayValue(metric)),
            itemStyle: { color: '#2457D6', borderRadius: [5, 5, 0, 0] },
          },
          {
            name: 'Comparison', type: 'bar', cursor: 'pointer', barMaxWidth: 46,
            data: current.map(metric => this.displayValue(comparisonByKey.get(metric.key))),
            itemStyle: { color: comparisonColor, borderRadius: [5, 5, 0, 0] },
          },
        ],
      };
      return;
    }

    this.options = {
      animation: false,
      // No legend on this branch, so the bottom gap only needs to clear the axis
      // labels themselves — containLabel already reserves those.
      grid: { left: 34, right: 20, top: 42, bottom: 8, containLabel: true },
      tooltip: {
        trigger: 'item',
        confine: true,
        backgroundColor: c.cardBg,
        borderColor: c.cardBorder,
        padding: [10, 12],
        textStyle: { color: c.textPrimary, fontSize: 12 },
        formatter: (params: any) => {
          const data = params?.data;
          if (!data || data.silent) return '';
          return `<div style="font-family: Inter, sans-serif;">
            <div style="font-weight:800; margin-bottom:4px;">${params.name}</div>
            <div style="color:${c.textSecondary}; font-weight:600;">
              ${this.formatExactCurrency(data.rawValue ?? 0)}
            </div>
          </div>`;
        },
      },
      xAxis: {
        type: 'category',
        data: steps.map(step => step.label),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: c.cardBorder } },
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          interval: 0,
          margin: 14,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: c.textSecondary,
          fontSize: 11,
          formatter: (value: number) => this.formatAxis(value),
        },
        splitLine: { lineStyle: { color: c.cardBorder, type: 'dashed' } },
      },
      series: [
        {
          name: 'offset',
          type: 'bar',
          stack: 'bridge',
          silent: true,
          itemStyle: { color: 'transparent' },
          emphasis: { disabled: true },
          data: steps.map(step => Math.min(step.start, step.end)),
        },
        {
          name: 'KPI bridge',
          type: 'bar',
          cursor: 'pointer',
          stack: 'bridge',
          barWidth: '68%',
          data: steps.map(step => ({
            value: Math.abs(step.end - step.start),
            rawValue: step.displayValue,
            itemStyle: { color: step.color, borderRadius: 5 },
            label: {
              show: true,
              position: 'top',
              color: c.textPrimary,
              fontSize: 11,
              fontWeight: 800,
              formatter: () => this.formatCurrency(step.displayValue),
            },
          })),
        },
      ],
    };
  }

  private buildSteps(): WaterfallStep[] {
    const c = chartColors();
    const colors: Record<string, string> = {
      revenue: c.positive || '#16A34A',
      nfi: kpiColor('nfi'),
      operating_cost: c.negative || '#DC2626',
      ebitda: kpiColor('ebitda_margin'),
      net_profit: kpiColor('net_profit_margin'),
    };

    return this.visibleMetrics()
      .map(metric => {
        const value = this.displayValue(metric);
        return {
          label: metric.key === 'operating_cost' ? 'Total Administrative Cost' : metric.name,
          start: 0,
          end: Math.abs(value),
          displayValue: value,
          color: colors[metric.key] ?? '#344B68',
        };
      });
  }

  private displayValue(metric: ProgressionFinancialMetric | undefined): number {
    const value = Number(metric?.value ?? 0);
    return metric && ['cost_of_sales', 'operating_cost'].includes(metric.key)
      ? Math.abs(value)
      : value;
  }

  private visibleMetrics(): ProgressionFinancialMetric[] {
    const metrics = [...this.metrics]
      .filter(metric => Number.isFinite(Number(metric.value)) && Number(metric.value) !== 0)
      .sort((left, right) => left.displayOrder - right.displayOrder);

    const nfiIndex = metrics.findIndex(metric => metric.key === 'nfi');
    const operatingCostIndex = metrics.findIndex(metric => metric.key === 'operating_cost');
    if (nfiIndex >= 0 && operatingCostIndex >= 0) {
      [metrics[nfiIndex], metrics[operatingCostIndex]] = [
        metrics[operatingCostIndex],
        metrics[nfiIndex],
      ];
    }

    return metrics;
  }

  private visibleComparisonMetrics(): ProgressionFinancialMetric[] {
    const comparisonByKey = new Map(this.comparisonMetrics.map(metric => [metric.key, metric]));
    return this.visibleMetrics()
      .map(metric => comparisonByKey.get(metric.key))
      .filter((metric): metric is ProgressionFinancialMetric => !!metric);
  }

  private formatAxis(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}m`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
    return `${value}`;
  }

  private formatCurrency(value: number): string {
    const abs = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(2)}K`;
    return `${sign}${abs.toFixed(0)}`;
  }

  private formatExactCurrency(value: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);
  }
}
